// Promise-based client for the worker-backed parser.
//
// One worker instance, created lazily on first use. Requests are
// tagged with a monotonically-incrementing id so multiple in-flight
// parses don't mix up their responses. When the worker API isn't
// available (e.g., inside vitest without the DOM), falls back to
// the synchronous parser on the main thread.
//
// A module-level LRU cache on code text short-circuits the parse
// entirely for strings we've seen recently — relevant when the user
// toggles tabs without editing, navigates between songs, or undoes
// back to a prior state. Cached entries are safe to share because
// edits go through immer's `produce` which never mutates the input.

import { parseCode } from './parser';
import type { ParseResult } from './types';

type Response = { id: number; result: ParseResult };

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, (result: ParseResult) => void>();

// Bounded LRU — Map preserves insertion order, so re-inserting on
// get bumps recency and the oldest entry is always first.
const PARSE_CACHE_SIZE = 64;
const parseCache = new Map<string, ParseResult>();

function cacheGet(code: string): ParseResult | undefined {
  const hit = parseCache.get(code);
  if (hit === undefined) return undefined;
  // Bump recency.
  parseCache.delete(code);
  parseCache.set(code, hit);
  return hit;
}

function cachePut(code: string, result: ParseResult): void {
  if (parseCache.has(code)) parseCache.delete(code);
  parseCache.set(code, result);
  while (parseCache.size > PARSE_CACHE_SIZE) {
    const oldest = parseCache.keys().next().value;
    if (oldest === undefined) break;
    parseCache.delete(oldest);
  }
}

// Exposed for tests.
export function __clearParseCacheForTests(): void {
  parseCache.clear();
}

// Sync cache-only lookup. Returns the previously-parsed result for
// `code` if it's still in the LRU, otherwise `undefined`. Never
// triggers a fresh parse — callers that need a guaranteed result
// should fall back to `parseCode` themselves.
//
// The reason this exists: the block editor populates the cache via
// `parseCodeAsync`, and the parsed Song holds the exact ids that the
// rendered blocks were assigned. Other consumers (e.g. the play
// pipeline resolving a soloed block) must use the same Song object
// to keep ids stable — re-parsing mints fresh ids every time.
export function parseCodeCachedSync(code: string): ParseResult | undefined {
  return cacheGet(code);
}

function supportsWorkers(): boolean {
  return typeof Worker !== 'undefined' && typeof window !== 'undefined';
}

function getWorker(): Worker | null {
  if (!supportsWorkers()) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./parser.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.addEventListener('message', (e: MessageEvent<Response>) => {
      const { id, result } = e.data;
      const resolve = pending.get(id);
      if (resolve) {
        pending.delete(id);
        resolve(result);
      }
    });
    worker.addEventListener('error', (e) => {
      // Fail all pending requests with a synthesised error.
      console.error('[block parser worker]', e);
      for (const [, resolve] of pending) {
        resolve({
          ok: false,
          reason: 'Worker crashed',
          location: { from: 0, to: 0 },
        });
      }
      pending.clear();
      // The resolvers above route through cachePut, so the synthetic
      // error would poison the cache for whatever code was in flight.
      // Clear the whole cache — rare event, cheap correctness.
      parseCache.clear();
      worker = null;
    });
    return worker;
  } catch (e) {
    console.warn('[block parser] worker unavailable, falling back sync:', e);
    worker = null;
    return null;
  }
}

export function parseCodeAsync(code: string): Promise<ParseResult> {
  const cached = cacheGet(code);
  if (cached !== undefined) return Promise.resolve(cached);

  const w = getWorker();
  if (!w) {
    // Synchronous fallback — either the environment doesn't support
    // workers, or construction failed. Wrap in a resolved promise so
    // the API stays consistent.
    const result = parseCode(code);
    cachePut(code, result);
    return Promise.resolve(result);
  }
  return new Promise((resolve) => {
    const id = nextId++;
    pending.set(id, (result) => {
      cachePut(code, result);
      resolve(result);
    });
    w.postMessage({ id, code });
  });
}
